import { __assign, __decorate, __metadata, __rest } from "tslib";
import { Actions, Effect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { ServerError } from '../../../models/Error';
import * as fromSettings from '../../../settings/reducers';
import { AddMessageToThread, LoadNewestMessages } from '../actions/load-message.actions';
import { SetSelectedTab } from '../actions/message-ui.actions';
import { NewMessageActionTypes, SendNewMessageError, SendNewMessageSuccess, ShowRerouteToast } from '../actions/new-message.actions';
import * as fromMessages from '../reducers';
import { MessageService } from '../services/message.service';
import { ToastOptions } from 'src/app/models/ToastOptions';
import { reportError } from 'src/app/core/services/sentry.utils';
var NewMessageEffects = /** @class */ (function () {
    function NewMessageEffects(actions$, toastr, messageService, _store) {
        var _this = this;
        this.actions$ = actions$;
        this.toastr = toastr;
        this.messageService = messageService;
        this._store = _store;
        this._store
            .pipe(select(fromSettings.getSectionTranslations('MessageToast')))
            .subscribe(function (val) {
            _this._toastText = val;
        });
    }
    // Send New Message
    NewMessageEffects.prototype.sendNewMessage$ = function () {
        var _this = this;
        return this.actions$.pipe(ofType(NewMessageActionTypes.SendNewMessage), switchMap(function (action) {
            return _this.messageService
                .addMessageToThread(action.payload.newMessage)
                .pipe(mergeMap(function (response) {
                var result = __assign(__assign({}, action.payload), { message: __assign(__assign({}, action.payload.message), { MessageItemId: response }) });
                return [
                    new AddMessageToThread(result),
                    new SendNewMessageSuccess({
                        mesageStarringEnabled: action.payload.messageStarringEnabled
                    })
                ];
            }), catchError(function (err) {
                reportError(err, 'effect.sendNewMessage');
                _this.toastr.show(_this._toastText.ErrorSending(action.payload.message.PatientFirstName +
                    ' ' +
                    action.payload.message.PatientLastName), _this._toastText.ErrorEncountered, {
                    timeOut: 0,
                    closeButton: true,
                    toastClass: "toast custom-toast error"
                });
                return [
                    new SendNewMessageError(new ServerError('[NewMessage]', 'Error sending new message', action.type))
                ];
            }));
        }));
    };
    // Create new Thread (outbound messaging)
    NewMessageEffects.prototype.sendNewThread$ = function () {
        var _this = this;
        return this.actions$.pipe(ofType(NewMessageActionTypes.SendNewThread), withLatestFrom(this._store.pipe(select(fromMessages.getCurrentTab))), mergeMap(function (_a) {
            var action = _a[0], tab = _a[1];
            var _b = action.payload.data, newMessage = _b.newMessage, messageInfo = _b.messageInfo;
            var patientName = newMessage.patientName, rest = __rest(newMessage, ["patientName"]);
            var toast = action.payload.toast;
            return _this.messageService.createNewThread(rest).pipe(mergeMap(function (newThread) {
                return [
                    new ShowRerouteToast({
                        toast: toast.success,
                        route: '/messages',
                        other: __assign(__assign(__assign(__assign({}, rest), newMessage), messageInfo), { id: newThread.NewMessageId }),
                        afterAction: new SetSelectedTab({
                            tab: tab,
                            params: {
                                MessageTypeId: newMessage.MessageTypeId,
                                FetchAfterMessageItemId: null,
                                FetchBeforeMessageItemId: null
                            }
                        })
                    }),
                    new SendNewMessageSuccess({
                        mesageStarringEnabled: action.payload.messageStarringEnabled
                    })
                ];
            }), catchError(function (err) {
                reportError(err, 'effect.sendNewThread');
                return [
                    new SendNewMessageError(new ServerError('[NewMessage]', 'Error sending new message to new conversation', action.type)),
                    new ShowRerouteToast({
                        toast: toast.error
                    })
                ];
            }));
        }));
    };
    NewMessageEffects.prototype.sendNewMessageSuccess = function () {
        return this.actions$.pipe(ofType(NewMessageActionTypes.SendNewMessageSuccess), withLatestFrom(this._store.pipe(select(fromMessages.getMessageList)), this._store.pipe(select(fromMessages.getNextPageToLoad))), switchMap(function (_a) {
            var action = _a[0], messages = _a[1], nextPageToLoad = _a[2];
            var latestMessageItemId = messages.length > 0 ? messages[0].MessageItemId : 0;
            return [
                new LoadNewestMessages({
                    fetchParams: __assign(__assign({}, nextPageToLoad), { FetchBeforeMessageItemId: null, FetchAfterMessageItemId: latestMessageItemId }),
                    showPushNotification: false
                })
            ];
        }));
    };
    NewMessageEffects.prototype.showNewConversationToastV3 = function () {
        var _this = this;
        return this.actions$.pipe(ofType(NewMessageActionTypes.ShowNewConversationToastV3), mergeMap(function (action) {
            var _a = action.payload, conversationId = _a.conversationId, folderId = _a.folderId, patients = _a.patients;
            var patientNames = patients
                .map(function (patient) { return patient.firstName + " " + patient.lastName; })
                .join(', ');
            var toast = _this.toastr.show(_this._toastText.SendingOutboundBody(patientNames), _this._toastText.SendingOutboundTitle, {
                timeOut: 0,
                closeButton: false,
                toastClass: "toast custom-toast info"
            });
            var success = new ToastOptions({
                toast: toast,
                toastRef: toast.toastRef.componentInstance,
                undoAction: null,
                title: _this._toastText.OutboundSentTitle,
                message: _this._toastText.OutboundSentBody(patientNames),
                type: 'success',
                options: {}
            });
            return [
                new ShowRerouteToast({
                    toast: success,
                    route: "/messages?folderIds=" + folderId + "&isResolved=false&isStarred=false&conversationId=" + conversationId,
                    other: {}
                })
            ];
        }));
    };
    __decorate([
        Effect(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", Observable)
    ], NewMessageEffects.prototype, "sendNewMessage$", null);
    __decorate([
        Effect(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", Observable)
    ], NewMessageEffects.prototype, "sendNewThread$", null);
    __decorate([
        Effect(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", Observable)
    ], NewMessageEffects.prototype, "sendNewMessageSuccess", null);
    __decorate([
        Effect(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", Observable)
    ], NewMessageEffects.prototype, "showNewConversationToastV3", null);
    return NewMessageEffects;
}());
export { NewMessageEffects };
